'use client';

import { FC, ReactNode, useRef, useState } from 'react';

import { IconCaretDown } from 'src/general/Icons/IconCaretDown';
import { IconCaretUp } from 'src/general/Icons/IconCaretUp';
import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';

import { clsx } from 'clsx';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import styles from './ServiceHistory.module.scss';

const HEADER_OFFSET = 130;

type ServiceHistoryRecord = {
  serviceDate?: string;
  kilometres?: number;
  recordFormat?: string;
  provider: string;
};

type Props = {
  serviceHistory?: ServiceHistoryRecord[];
  microcopy: {
    defaultHeader: ReactNode;
    sectionHeader: ReactNode;
    oneServiceHeader: ReactNode;
    likeNewHeader: ReactNode;
    showButtonLabel: ReactNode;
    hideButtonLabel: ReactNode;
    noServiceDateMessage: ReactNode;
    noKilometreMessage: ReactNode;
  };
};

export const ServiceHistory: FC<Props> = ({ serviceHistory, microcopy }) => {
  const [isShowList, setIsShowList] = useState(false);
  const latestItem = serviceHistory?.[0] ?? null;

  const header =
    serviceHistory && serviceHistory.length > 0
      ? serviceHistory.length > 1
        ? microcopy.defaultHeader
        : microcopy.oneServiceHeader
      : microcopy.likeNewHeader;

  const containerRef = useRef<null | HTMLHeadingElement>(null);

  const toggleShowList = () => {
    if (!isShowList && containerRef.current) {
      const accordionPosition = containerRef.current?.getBoundingClientRect().top;
      window.scrollTo({
        top: accordionPosition + window.scrollY - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
    setIsShowList((isShowList) => !isShowList);
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'accordion',
      value: 'view_service_history',
    });
  };

  return (
    <>
      <div className={styles.header}>
        <h3 className={clsx('c-fw-bold', 'c-fs-h5')} ref={containerRef}>
          {microcopy.sectionHeader}
        </h3>
      </div>
      <section className={styles.root}>
        <div className={styles.content}>
          <div className={styles.lastServiceContainer}>
            <BadgeIllustration
              background="none"
              shape="square"
              icon={BADGE.CARMA_TOUCH}
              className={styles.badgeIllustration}
            />
            <div className={styles.headerContainer}>
              <p className={clsx('c-fw-normal', 'c-fs-body2')}>{header}</p>
              {latestItem && (
                <div className={clsx(styles.latestItem, 'c-fw-bold', 'c-fs-body2')}>
                  <p data-testid="ServiceHistory-latestServiceDate" className={styles.serviceDate}>
                    {latestItem?.serviceDate ?? microcopy.noServiceDateMessage}
                  </p>
                  <p>
                    {latestItem?.kilometres
                      ? formatNumber(latestItem.kilometres, { append: 'km' })
                      : microcopy.noKilometreMessage}
                  </p>
                </div>
              )}
            </div>
          </div>
          {serviceHistory && serviceHistory.length > 1 && (
            <>
              <div className={styles.buttonContainer}>
                <Button
                  data-testid="ServiceHistory-expand"
                  variant="tertiary"
                  endIcon={isShowList ? <IconCaretUp /> : <IconCaretDown />}
                  onClick={toggleShowList}
                >
                  {isShowList ? microcopy.hideButtonLabel : microcopy.showButtonLabel}
                </Button>
              </div>
              {isShowList && (
                <div className={styles.listContainer}>
                  {serviceHistory.map((item) => (
                    <div className={styles.listItem} key={item.provider}>
                      <p className={clsx('c-fw-normal', 'c-fs-body2')}>
                        {item?.serviceDate ?? microcopy.noServiceDateMessage}
                      </p>
                      <p className={clsx(styles.itemContent, 'c-fw-bold', 'c-fs-body2')}>
                        {item.kilometres
                          ? formatNumber(item.kilometres, { append: 'km' })
                          : microcopy.noKilometreMessage}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};
